import { css } from '@emotion/react';
import React, { AllHTMLAttributes, CSSProperties } from 'react';
import { breakpoints, colors } from '../../../utils/styleguide';
import { captureException } from '../../../utils/tracking/sentry/sentry';

export const DEPRECATED_VARIANTS = ['handTitle', 'handSubtitle', 'hand', 'handSmall'];

/*
  Font weight scale (Sharp Grotesk):

  900 Black
  700 Bold
  600 SmBold
  500 Medium
  400 Book
  300 Light
  100 Thin
*/

export const styles = {
  preamble: css`
    margin: 0;
    font-family: Sharp-Grotesk-Wide;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    ${breakpoints.desktop} {
      font-size: 20px;
      line-height: 32px;
    }
  `,
  preambleSmall: css`
    margin: 0;
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  `,
  smallText: css`
    margin: 0;
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 10px;
    line-height: 16px;
    ${breakpoints.desktop} {
      font-size: 12px;
    }
  `,
  smallTextBold: css`
    margin: 0;
    font-family: Sharp-Grotesk;
    font-weight: 500;
    font-size: 10px;
    line-height: 16px;
    ${breakpoints.desktop} {
      font-size: 12px;
    }
  `,
  inputField: css`
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  `,
  label: css`
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  `,
  buttonLarge: css`
    margin: 0;
    font-family: Sharp-Grotesk-Wide;
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    cursor: pointer;
  `,
  buttonSmall: css`
    margin: 0;
    font-family: Sharp-Grotesk-Wide;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  `,
  bodyBold: css`
    font-family: Sharp-Grotesk;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    ${breakpoints.desktop} {
      font-size: 18px;
      line-height: 28px;
    }
  `,
  bodySmall: css`
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    ${breakpoints.desktop} {
      font-size: 14px;
      line-height: 20px;
    }
  `,
  bodySmallBold: css`
    font-family: Sharp-Grotesk;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    ${breakpoints.desktop} {
      font-size: 14px;
      line-height: 20px;
    }
  `,
  detail: css`
    font-family: Sharp-Grotesk;
    font-weight: 400;
    font-size: 10px;
    line-height: 16px;
    ${breakpoints.desktop} {
      font-size: 12px;
      line-height: 20px;
    }
  `,
};

export const variants = {
  titleDisplay: {
    tag: 'h1',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 36px;
      line-height: 48px;
      letter-spacing: -0.01em;
      ${breakpoints.tablet} {
        font-size: 56px;
        line-height: 64px;
        letter-spacing: -0.02em;
      }
      ${breakpoints.desktop} {
        font-size: 88px;
        line-height: 104px;
        letter-spacing: -0.02em;
      }
    `,
  },
  titleH1: {
    tag: 'h1',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 36px;
      line-height: 48px;
      letter-spacing: -0.01em;
      ${breakpoints.tablet} {
        font-size: 56px;
        line-height: 64px;
        letter-spacing: -0.02em;
      }
      ${breakpoints.desktop} {
        font-size: 88px;
        line-height: 104px;
        letter-spacing: -0.02em;
      }
    `,
  },
  title: {
    tag: 'h2',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 64px;
        line-height: 80px;
      }
    `,
  },
  titleH2: {
    tag: 'h2',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 64px;
        line-height: 80px;
      }
    `,
  },
  campaignTitle: {
    tag: 'h1',
    css: css`
      margin: 0;
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 60px;
      line-height: 72px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 104px;
        line-height: 125px;
      }
    `,
  },
  causeDescription: {
    tag: 'h3',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 48px;
        line-height: 56px;
      }
    `,
  },
  titleH3: {
    tag: 'h3',
    css: css`
      margin: 0;
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 40px;
        line-height: 56px;
      }
    `,
  },
  titleH4: {
    tag: 'h4',
    css: css`
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.01em;
      ${breakpoints.desktop} {
        font-size: 24px;
        line-height: 36px;
      }
    `,
  },
  titleH5: {
    tag: 'h5',
    css: css`
      font-family: Sharp-Grotesk;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;
      color: ${colors.darkGrey};
      ${breakpoints.desktop} {
        font-size: 20px;
        line-height: 32px;
      }
    `,
  },
  titleH4XL: {
    tag: 'h4',
    css: css`
      margin: 0;
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.01em;
      ${breakpoints.xl} {
        font-size: 36px;
        line-height: 44px;
      }
    `,
  },
  body: {
    tag: 'p',
    css: css`
      font-family: Sharp-Grotesk;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      ${breakpoints.desktop} {
        font-size: 18px;
        line-height: 28px;
      }
    `,
  },
  bodySmall: {
    tag: 'p',
    css: styles.bodySmall,
  },
  bodyBold: {
    tag: 'p',
    css: styles.bodyBold,
  },
  bodySmallBold: {
    tag: 'p',
    css: styles.bodySmallBold,
  },
  preamble: {
    tag: 'p',
    css: styles.preamble,
  },
  preambleSmall: {
    tag: 'p',
    css: styles.preambleSmall,
  },
  smallText: {
    tag: 'p',
    css: styles.smallText,
  },
  smallTextBold: {
    tag: 'p',
    css: styles.smallTextBold,
  },
  buttonLarge: {
    tag: 'button',
    css: styles.buttonLarge,
  },
  buttonLargeP: {
    tag: 'p',
    css: styles.buttonLarge,
  },
  buttonSmall: {
    tag: 'button',
    css: styles.buttonSmall,
  },
  callToAction: {
    tag: 'a',
    css: css`
      text-decoration: none;
      font-family: Sharp-Grotesk-Wide;
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
    `,
  },
  scribbleDisplay: {
    tag: 'span',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 60px;
      font-weight: 400;
      line-height: 52px;
      ${breakpoints.desktop} {
        font-size: 108px;
        line-height: 92px;
      }
    `,
  },
  h1Hand: {
    tag: 'h1',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 36px;
      line-height: 48px;
      font-weight: 400;
      ${breakpoints.tablet} {
        font-size: 56px;
        line-height: 64px;
        letter-spacing: -0.02em;
      }
      ${breakpoints.desktop} {
        font-size: 88px;
        line-height: 104px;
      }
    `,
  },
  handTitle: {
    // Deprecated
    tag: 'h1',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 36px;
      line-height: 48px;
      font-weight: 400;
      ${breakpoints.tablet} {
        font-size: 56px;
        line-height: 64px;
        letter-spacing: -0.02em;
      }
      ${breakpoints.desktop} {
        font-size: 88px;
        line-height: 104px;
      }
    `,
  },
  h2Hand: {
    tag: 'h2',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 32px;
      font-weight: 400;
      line-height: 40px;
      ${breakpoints.desktop} {
        font-size: 64px;
        line-height: 80px;
      }
    `,
  },
  handSubtitle: {
    // Deprecated
    tag: 'h2',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 32px;
      font-weight: 400;
      line-height: 40px;
      ${breakpoints.desktop} {
        font-size: 64px;
        line-height: 80px;
      }
    `,
  },
  h3Hand: {
    tag: 'h3',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 24px;
      line-height: 28px;
      ${breakpoints.desktop} {
        font-size: 40px;
        line-height: 56px;
      }
    `,
  },
  hand: {
    // Deprecated
    tag: 'h3',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 24px;
      line-height: 28px;
      ${breakpoints.desktop} {
        font-size: 40px;
        line-height: 56px;
      }
    `,
  },
  h4Hand: {
    tag: 'h4',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 20px;
      line-height: 28px;
      ${breakpoints.desktop} {
        font-size: 24px;
        line-height: 36px;
      }
    `,
  },
  handSmall: {
    // Deprecated
    tag: 'h4',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 20px;
      line-height: 28px;
      ${breakpoints.desktop} {
        font-size: 24px;
        line-height: 36px;
      }
    `,
  },
  handQuote: {
    tag: 'span',
    css: css`
      font-family: MilkywireHand-Regular;
      font-size: 44px;
      line-height: 56px;
    `,
  },
  inputField: {
    tag: 'span',
    css: styles.inputField,
  },
  label: {
    tag: 'span',
    css: styles.label,
  },
  detail: {
    tag: 'p',
    css: styles.detail,
  },
};

export type TypographyVariants = keyof typeof variants;

export type TypographyProps = AllHTMLAttributes<HTMLElement> & {
  variant?: TypographyVariants;
  tag?: string;
  value?: string;
  svgName?: string;
  // NB: Do not use in new code, this only exists for legacy code
  underlineColor?: string;
  useBuiltInMargins?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
};

const resetMargin = css`
  margin: 0;
`;

const Typography = React.forwardRef<React.ReactNode, TypographyProps>(
  (
    {
      variant = 'body',
      tag,
      children,
      color = colors.black,
      className,
      href,
      onClick,
      type,
      value,
      useBuiltInMargins,
      disabled,
      style,
      ...rest
    },
    ref,
  ) => {
    delete rest.svgName;
    delete rest.underlineColor;

    let variantData = variants[variant];

    if (!variantData) {
      variantData = variants.body;
      captureException(
        new Error(`Typography variant ${variant} does not exist. Using "body" instead.`),
      );
    }

    let Tag = tag as React.ElementType;
    if (tag === undefined) {
      Tag = variantData.tag as React.ElementType;
    }
    return (
      <Tag
        ref={ref}
        css={[
          !useBuiltInMargins && resetMargin,
          css`
            color: ${color};
            text-decoration: none;
            font-family: Helvetica, Arial, sans-serif;
          `,
          variantData ? variantData.css : '',
        ]}
        className={className}
        href={href}
        onClick={onClick}
        type={type}
        value={value}
        disabled={disabled}
        style={style}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
